import { create } from '@bufbuild/protobuf'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { Transport } from '@connectrpc/connect'

import { getEvent, getBooking, getInquiry, getQuote, getContact, getInvoice } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetEventResponseSchema,
  GetBookingResponseSchema,
  GetInquiryResponseSchema,
  GetQuoteResponseSchema,
  GetContactResponseSchema,
  GetInvoiceResponseSchema,
} from '@/gen/cortado/v1/cortado_pb'
import type { GetEventDetailResponse, GetQuoteDetailResponse, GetInvoiceDetailResponse } from '@/gen/cortado/v1/cortado_pb'
import { createQueryKeyForGet } from '@/lib/query-utils'

/**
 * Generic aggregate -> entity-cache seeding pattern for detail routes.
 *
 * A detail route's component hooks (useEvent / useBooking / useInquiry) each read
 * their own single-entity query cache. Rather than let them fire a sequential
 * request waterfall, an aggregate RPC (GetEventDetail) fetches all three entities
 * in one round-trip and this helper hydrates each single-entity cache so the hooks
 * resolve from a warm cache.
 *
 * Each seed is staleness-guarded: if the target cache already holds data at least
 * as fresh as `updatedAt`, it is left untouched — so aggregate data never clobbers
 * newer entity data (e.g. an in-flight mutation result). `updatedAt` is threaded
 * into `setQueryData` so the seeded entry keeps an honest staleness clock.
 */
export function seedEventDetailCaches(
  queryClient: QueryClient,
  transport: Transport,
  response: GetEventDetailResponse,
  updatedAt: number,
) {
  const { event, booking, inquiry } = response

  if (event?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getEvent, transport, input: { id: event.id } }),
      create(GetEventResponseSchema, { event }),
      updatedAt,
      event.updatedAt,
      (existing) => (existing as { event?: { updatedAt?: ServerTimestamp } })?.event?.updatedAt,
    )
  }

  if (booking?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getBooking, transport, input: { id: booking.id } }),
      create(GetBookingResponseSchema, { booking }),
      updatedAt,
      booking.updatedAt,
      (existing) => (existing as { booking?: { updatedAt?: ServerTimestamp } })?.booking?.updatedAt,
    )
  }

  if (inquiry?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getInquiry, transport, input: { id: inquiry.id } }),
      create(GetInquiryResponseSchema, { inquiry }),
      updatedAt,
      inquiry.updatedAt,
      (existing) => (existing as { inquiry?: { updatedAt?: ServerTimestamp } })?.inquiry?.updatedAt,
    )
  }
}

/**
 * Aggregate -> entity-cache seeding for the quote detail route.
 *
 * The quote detail page's component hooks (useQuote / useInquiry / useContact /
 * useBooking) each read their own single-entity cache. GetQuoteDetail fetches
 * all of them in one round-trip; this helper hydrates each single-entity cache
 * so the hooks resolve warm, collapsing the old
 * quote -> inquiry -> booking waterfall. Each seed is staleness-guarded so
 * aggregate data never clobbers newer entity data.
 *
 * Note: the aggregate's `convertedInvoice` (an InvoiceLite) is consumed directly
 * from the GetQuoteDetail cache by the page and is intentionally not seeded here.
 */
export function seedQuoteDetailCaches(
  queryClient: QueryClient,
  transport: Transport,
  response: GetQuoteDetailResponse,
  updatedAt: number,
) {
  const { quote, inquiry, contact, booking } = response

  if (quote?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getQuote, transport, input: { id: quote.id } }),
      create(GetQuoteResponseSchema, { quote }),
      updatedAt,
      quote.updatedAt,
      (existing) => (existing as { quote?: { updatedAt?: ServerTimestamp } })?.quote?.updatedAt,
    )
  }

  if (inquiry?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getInquiry, transport, input: { id: inquiry.id } }),
      create(GetInquiryResponseSchema, { inquiry }),
      updatedAt,
      inquiry.updatedAt,
      (existing) => (existing as { inquiry?: { updatedAt?: ServerTimestamp } })?.inquiry?.updatedAt,
    )
  }

  if (contact?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getContact, transport, input: { id: contact.id } }),
      create(GetContactResponseSchema, { contact }),
      updatedAt,
      contact.updatedAt,
      (existing) => (existing as { contact?: { updatedAt?: ServerTimestamp } })?.contact?.updatedAt,
    )
  }

  if (booking?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getBooking, transport, input: { id: booking.id } }),
      create(GetBookingResponseSchema, { booking }),
      updatedAt,
      booking.updatedAt,
      (existing) => (existing as { booking?: { updatedAt?: ServerTimestamp } })?.booking?.updatedAt,
    )
  }
}

/**
 * Aggregate -> entity-cache seeding for the invoice detail route.
 *
 * The invoice detail page's component hooks (useInvoice / useContact /
 * useBooking) each read their own single-entity cache. GetInvoiceDetail fetches
 * all of them in one round-trip; this helper hydrates each single-entity cache
 * so the hooks resolve warm, collapsing the old invoice -> contact / booking
 * waterfall. Each seed is staleness-guarded so aggregate data never clobbers
 * newer entity data.
 */
export function seedInvoiceDetailCaches(
  queryClient: QueryClient,
  transport: Transport,
  response: GetInvoiceDetailResponse,
  updatedAt: number,
) {
  const { invoice, contact, booking } = response

  if (invoice?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getInvoice, transport, input: { id: invoice.id } }),
      create(GetInvoiceResponseSchema, { invoice }),
      updatedAt,
      invoice.updatedAt,
      (existing) => (existing as { invoice?: { updatedAt?: ServerTimestamp } })?.invoice?.updatedAt,
    )
  }

  if (contact?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getContact, transport, input: { id: contact.id } }),
      create(GetContactResponseSchema, { contact }),
      updatedAt,
      contact.updatedAt,
      (existing) => (existing as { contact?: { updatedAt?: ServerTimestamp } })?.contact?.updatedAt,
    )
  }

  if (booking?.id) {
    seedEntityCache(
      queryClient,
      createQueryKeyForGet({ schema: getBooking, transport, input: { id: booking.id } }),
      create(GetBookingResponseSchema, { booking }),
      updatedAt,
      booking.updatedAt,
      (existing) => (existing as { booking?: { updatedAt?: ServerTimestamp } })?.booking?.updatedAt,
    )
  }
}

/**
 * Seeds a single-entity query cache, but only if the aggregate data is at least
 * as fresh as whatever is already cached. Never overwrites fresher entity data.
 */
type ServerTimestamp = { seconds: bigint; nanos: number }

function compareServerTimestamps(a: ServerTimestamp, b: ServerTimestamp): number {
  if (a.seconds !== b.seconds) return a.seconds > b.seconds ? 1 : -1
  return a.nanos === b.nanos ? 0 : a.nanos > b.nanos ? 1 : -1
}

export function shouldSeedEntityCache(
  existingServerUpdatedAt: ServerTimestamp | undefined,
  incomingServerUpdatedAt: ServerTimestamp | undefined,
  existingClientUpdatedAt: number | undefined,
  incomingClientUpdatedAt: number,
): boolean {
  if (existingServerUpdatedAt && incomingServerUpdatedAt) {
    const comparison = compareServerTimestamps(existingServerUpdatedAt, incomingServerUpdatedAt)
    if (comparison !== 0) return comparison < 0
  }

  return existingClientUpdatedAt === undefined || existingClientUpdatedAt < incomingClientUpdatedAt
}

function seedEntityCache(
  queryClient: QueryClient,
  queryKey: QueryKey,
  data: unknown,
  updatedAt: number,
  incomingServerUpdatedAt: ServerTimestamp | undefined,
  getExistingServerUpdatedAt: (data: unknown) => ServerTimestamp | undefined,
) {
  const existingData = queryClient.getQueryData(queryKey)
  const existingUpdatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt
  const existingServerUpdatedAt = existingData
    ? getExistingServerUpdatedAt(existingData)
    : undefined

  if (!shouldSeedEntityCache(
    existingServerUpdatedAt,
    incomingServerUpdatedAt,
    existingUpdatedAt,
    updatedAt,
  )) {
    return
  }

  queryClient.setQueryData(queryKey, data, { updatedAt })
}
